import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Link from "next/link";
import { format, parseISO, addMinutes } from "date-fns";
import {
  BookOpen,
  Calendar,
  Clock,
  Bell,
  BellRing,
  BellOff,
  ChevronDown,
  GraduationCap,
  BookOpen as Book,
  CalendarIcon,
  CheckCircle,
  XCircle,
  Loader2,
  Check,
  CheckCheck,
  ArrowRight,
  Menu as MenuIcon,
  PlayCircle,
} from "lucide-react";
import {
  subscribeToLiveClass,
  unsubscribeFromLiveClass,
  subscribeToSubject,
  subscribeToGrade,
  checkSubscriptionStatus,
  unsubscribeFromSubject,
  unsubscribeFromGrade,
} from "../../services/notificationService";
import { UserContext } from "../../context";
import { createPortal } from "react-dom";
import { useSubscriptionStatus } from "./hooks/useSubscriptionStatus";
import { useNotificationMutations } from "./hooks/useNotificationMutations";

// Background color mapping for different subjects (fallback if gradients aren't provided)
const subjectColors = {
  Chemistry: { start: "rgb(255, 87, 31)", end: "rgb(255, 138, 92)" },
  Physics: { start: "#2b86c5", end: "#5ab2f7" },
  Biology: { start: "#3da35a", end: "#8eda85" },
  Maths: { start: "rgb(251, 139, 35)", end: "rgb(255, 221, 0)" },
  English: { start: "#9c3fb4", end: "#c278d8" },
  Hindi: { start: "#e44858", end: "#ff7886" },
  "Social Studies": { start: "#2d7a78", end: "#5eb8b6" },
  "Computer Science": { start: "#5246c7", end: "#8075ff" },
};

// Default colors for subjects not in the mapping
const defaultColors = { start: "#6941c6", end: "#9f7aea" };

// Helper function to capitalize first letter of each word
const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export interface LiveClassProps {
  id: string;
  title: string;
  subject: string;
  topic: string;
  instructor: string;
  start_time: string;
  duration: number;
  status: string;
  meeting_link: string;
  gradient_start?: string;
  gradient_end?: string;
  grade?: string;
}

// Subscription type enum
type SubscriptionType = "session" | "subject" | "grade";

export default function LiveClassCard({
  classData,
}: {
  classData: LiveClassProps;
}) {
  const {
    id,
    title,
    subject,
    topic,
    start_time,
    duration,
    meeting_link,
    gradient_start,
    gradient_end,
    grade = "class_9", // Default grade if not provided
    status = "initial", // Default status if not provided
  } = classData;

  // Get user from context
  const [user] = useContext(UserContext).user;

  // State for notification status
  const [isNotifying, setIsNotifying] = useState<boolean>(false);
  const [notificationSuccess, setNotificationSuccess] = useState<
    boolean | null
  >(null);
  const [notificationError, setNotificationError] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");

  // State for subscription option menu
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notifyButtonRef = useRef<HTMLButtonElement>(null);

  // Portal and tooltip related refs and state
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const portalContainerRef = useRef<HTMLDivElement | null>(null);

  // Create tooltip container ref
  const tooltipRef = useRef<HTMLDivElement>(null);
  const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // State for local/inline toast
  const [showInlineToast, setShowInlineToast] = useState(false);
  const inlineToastRef = useRef<HTMLDivElement>(null);
  const inlineToastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Format the date and time
  const classDate = new Date(start_time);
  const formattedDate = format(classDate, "MMM d, yyyy");
  const formattedTime = format(classDate, "h:mm a");

  // Determine status mappings
  const isPast = status === "disposed";
  const isLive = status === "live";
  const isUpcoming = status === "initial";

  // Determine the status for display
  const displayStatus = isPast ? "ended" : isLive ? "live" : "upcoming";

  // Process title and topic text to ensure proper capitalization
  const formattedTitle = toTitleCase(title);
  const formattedSubject = toTitleCase(subject);

  // Use gradient colors from backend if available, otherwise fall back to subject-based colors
  let gradientStyle;
  if (gradient_start && gradient_end) {
    gradientStyle = {
      background: `linear-gradient(to right, ${gradient_start}, ${gradient_end})`,
    };
  } else {
    // Fall back to the subject color mapping if gradients aren't provided
    const colors = subjectColors[subject] || defaultColors;
    gradientStyle = {
      background: `linear-gradient(to right, ${colors.start}, ${colors.end})`,
    };
  }

  // Use React Query hooks for subscription status and mutations
  const {
    isSubscribed,
    subscriptionType,
    isLoading: isSubscriptionLoading,
    refetch: refetchSubscription,
  } = useSubscriptionStatus({
    email: user?.email || "",
    sessionId: id,
    subject,
    grade,
    enabled: !!user?.email,
  });

  // Use mutations for subscription actions
  const {
    subscribeToClass,
    unsubscribeFromClass,
    subscribeToSubjectClasses,
    unsubscribeFromSubjectClasses,
    subscribeToGradeClasses,
    unsubscribeFromGradeClasses,
  } = useNotificationMutations();

  // Function to save subscription status and type to localStorage
  const saveSubscriptionData = useCallback(
    (
      email: string,
      classId: string,
      isSubscribed: boolean,
      type: SubscriptionType | null
    ) => {
      try {
        // Save subscription status
        localStorage.setItem(
          `notification_${email}_${classId}`,
          isSubscribed ? "true" : "false"
        );

        // Save subscription type
        if (type) {
          localStorage.setItem(`notification_type_${email}_${classId}`, type);
        } else {
          localStorage.removeItem(`notification_type_${email}_${classId}`);
        }
      } catch (error) {
        console.warn(
          "Could not save subscription data to localStorage:",
          error
        );
      }
    },
    []
  );

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        notifyButtonRef.current &&
        !notifyButtonRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Create portal container on mount
  useEffect(() => {
    // Create portal container if it doesn't exist
    const portalId = "tooltip-portal-container";
    let container = document.getElementById(portalId) as HTMLDivElement;

    if (!container) {
      container = document.createElement("div");
      container.id = portalId;
      container.style.position = "fixed";
      container.style.top = "0";
      container.style.left = "0";
      container.style.width = "100%";
      container.style.height = "100vh";
      container.style.pointerEvents = "none";
      container.style.zIndex = "9999";
      document.body.appendChild(container);
    }

    portalContainerRef.current = container;

    // Cleanup on unmount
    return () => {
      // Don't remove the portal container as it might be used by other cards
      // Just handle our own state cleanup
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Update tooltip position whenever it should be visible
  useEffect(() => {
    if (showTooltip && notifyButtonRef.current) {
      updateTooltipPosition();
    }
  }, [showTooltip]);

  // Update position on window resize and scroll
  useEffect(() => {
    const handlePositionUpdate = () => {
      if (showTooltip) {
        updateTooltipPosition();
      }
    };

    window.addEventListener("resize", handlePositionUpdate);
    window.addEventListener("scroll", handlePositionUpdate, true);

    return () => {
      window.removeEventListener("resize", handlePositionUpdate);
      window.removeEventListener("scroll", handlePositionUpdate, true);
    };
  }, [showTooltip]);

  // Function to calculate and update tooltip position
  const updateTooltipPosition = useCallback(() => {
    if (!notifyButtonRef.current || !showTooltip) return;

    const buttonRect = notifyButtonRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Check if there's enough space above the button
    const spaceAbove = buttonRect.top;
    const spaceBelow = windowHeight - buttonRect.bottom;

    // Default position (above the button)
    let top = buttonRect.top - 10;

    // If there's not enough space above, position below
    if (spaceAbove < 100 && spaceBelow > spaceAbove) {
      top = buttonRect.bottom + 10;
    }

    setTooltipPosition({
      top,
      left: buttonRect.left + buttonRect.width / 2, // Center horizontally
    });
  }, [showTooltip]);

  // Handle showing tooltip
  const handleShowTooltip = useCallback(
    (message: string) => {
      setNotificationMessage(message);
      setShowTooltip(true);

      // Clear any existing timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }

      // Update position
      updateTooltipPosition();

      // Auto-hide after 4 seconds
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
      }, 4000);
    },
    [updateTooltipPosition]
  );

  // Clear all notification timers on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }
      if (inlineToastTimeoutRef.current) {
        clearTimeout(inlineToastTimeoutRef.current);
      }
    };
  }, []);

  // Show notification toast with smooth animation
  const showNotification = useCallback(
    (message: string, type: "success" | "error" | "info" = "info") => {
      setNotificationMessage(message);
      setShowInlineToast(true);

      // Clear any existing timeout
      if (inlineToastTimeoutRef.current) {
        clearTimeout(inlineToastTimeoutRef.current);
      }

      // Auto-hide after 4 seconds
      inlineToastTimeoutRef.current = setTimeout(() => {
        setShowInlineToast(false);
      }, 3500);

      // Set notification type
      if (type === "success") {
        setNotificationSuccess(true);
        setNotificationError(false);
      } else if (type === "error") {
        setNotificationSuccess(false);
        setNotificationError(true);
      } else {
        setNotificationSuccess(null);
        setNotificationError(false);
      }
    },
    []
  );

  // Toggle notification menu
  const handleToggleMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Always show menu, regardless of subscription status
    setShowMenu(!showMenu);
  };

  // Handle notification toggle
  const handleNotify = async (
    e: React.MouseEvent,
    unsubscribeType?: "session" | "subject" | "grade"
  ) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Prevent multiple clicks
      if (isNotifying) return;

      setIsNotifying(true);
      setNotificationSuccess(null);
      setNotificationError(false);
      setNotificationMessage("");
      setShowMenu(false);

      // Check if user is logged in and has email and uid
      if (!user || !user.email || !user.uid) {
        setNotificationError(true);
        showNotification("Please log in to receive notifications", "error");
        setIsNotifying(false);
        return;
      }

      // Common mutation variables
      const mutationVars = {
        email: user.email,
        userId: user.uid,
        name: user.displayName || undefined,
        grade,
      };

      // Handle subscription based on type
      if (isSubscribed) {
        // Unsubscribe based on selected type or default to session
        const type = unsubscribeType || "session";

        if (type === "subject") {
          // Unsubscribe from subject using mutation
          const result = await unsubscribeFromSubjectClasses.mutateAsync({
            ...mutationVars,
            subject,
          });

          if (result.success) {
            showNotification(
              `Unsubscribed from all ${formattedSubject} classes`,
              "success"
            );
          } else {
            throw new Error(
              result.message || "Failed to unsubscribe from subject"
            );
          }
        } else if (type === "grade") {
          // Unsubscribe from grade using mutation
          const result = await unsubscribeFromGradeClasses.mutateAsync(
            mutationVars
          );

          if (result.success) {
            showNotification(
              `Unsubscribed from all grade ${grade.replace(
                "class_",
                ""
              )} classes`,
              "success"
            );
          } else {
            throw new Error(
              result.message || "Failed to unsubscribe from grade"
            );
          }
        } else {
          // Unsubscribe from session using mutation
          const result = await unsubscribeFromClass.mutateAsync({
            ...mutationVars,
            sessionId: id,
          });

          if (result.success) {
            showNotification(
              "Notifications turned off for this class",
              "success"
            );
          } else {
            showNotification(
              result.message || "Failed to unsubscribe",
              "error"
            );
          }
        }
      } else {
        // Subscribe based on selected type
        if (unsubscribeType === "subject") {
          // Subscribe to subject using mutation
          const result = await subscribeToSubjectClasses.mutateAsync({
            ...mutationVars,
            subject,
          });

          if (result.success) {
            showNotification(
              `You'll be notified about all ${formattedSubject} classes`,
              "success"
            );
          } else {
            showNotification(result.message || "Failed to subscribe", "error");
          }
        } else if (unsubscribeType === "grade") {
          // Subscribe to grade using mutation
          const result = await subscribeToGradeClasses.mutateAsync(
            mutationVars
          );

          if (result.success) {
            showNotification(
              `You'll be notified about all classes for grade ${grade.replace(
                "class_",
                ""
              )}`,
              "success"
            );
          } else {
            showNotification(result.message || "Failed to subscribe", "error");
          }
        } else {
          // Subscribe to session using mutation
          const result = await subscribeToClass.mutateAsync({
            ...mutationVars,
            sessionId: id,
          });

          if (result.success) {
            showNotification(
              "You'll be notified before this class starts",
              "success"
            );
          } else {
            showNotification(result.message || "Failed to subscribe", "error");
          }
        }
      }
    } catch (error) {
      console.error("Error toggling notifications:", error);
      showNotification("An unexpected error occurred", "error");
    } finally {
      setIsNotifying(false);
    }
  };

  // Render inline notification toast (attached to the card)
  const InlineNotificationToast = () => {
    if (!showInlineToast || !notificationMessage) return null;

    return (
      <div ref={inlineToastRef} className="inline-notification mt-2">
        <div
          className={`inline-toast flex items-start justify-start gap-1 ${
            notificationError
              ? "error"
              : notificationSuccess
              ? "success"
              : "info"
          }`}
        >
          <span className="toast-icon h-[13px] !inline-flex items-center text-yellow-500">
            {notificationError && <XCircle size={12} />}
            {!notificationError && notificationSuccess && (
              <CheckCircle size={12} />
            )}
            {!notificationError && notificationSuccess === null && (
              <Bell size={12} />
            )}
          </span>
          <span className="toast-message text-yellow-400 !text-[12px]">
            {notificationMessage}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={cardRef}
      className={`live-class-card rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 w-full h-full bg-gray-800 border border-gray-700 hover:translate-y-[-2px] ${
        isPast
          ? "past-class"
          : isLive
          ? "active-live-session"
          : "upcoming-class"
      }`}
    >
      <div
        className="h-20 flex items-center justify-between p-3 sm:p-4 text-white relative"
        style={gradientStyle}
      >
        <div className="overflow-hidden">
          <h3 className="font-bold text-base sm:text-lg mb-0.5 text-white truncate">
            {formattedTitle}
          </h3>
          <p className="text-xs text-white opacity-90 font-medium truncate">
            {formattedSubject}
          </p>
        </div>
        <div className="bg-white bg-opacity-30 rounded-full p-1.5 flex-shrink-0 ml-2">
          <BookOpen size={18} className="text-white" />
        </div>

        {/* Status badge */}
        <div className={`status-badge status-${displayStatus}`}>
          {displayStatus === "live" ? (
            <>
              <span className="pulse-dot"></span> Live Now
            </>
          ) : displayStatus === "ended" ? (
            "Recording Available"
          ) : (
            "Upcoming"
          )}
        </div>
      </div>

      <div className="p-3">
        <div className="flex flex-col space-y-1.5">
          <div className="flex items-center text-xs text-gray-300">
            <Calendar size={14} className="mr-1.5 flex-shrink-0" />
            <span>{formattedDate}</span>
          </div>
          <div className="flex items-center text-xs text-gray-300">
            <Clock size={14} className="mr-1.5 flex-shrink-0" />
            <span>
              {formattedTime} • {duration} mins
            </span>
          </div>
        </div>

        <div className="flex gap-2 mt-2 relative">
          <div className="bell-container" ref={menuRef}>
            <button
              ref={notifyButtonRef}
              onClick={handleToggleMenu}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              type="button"
              className={`notification-btn flex items-center justify-center h-9 flex-shrink-0 rounded-md transition-all duration-300 ${
                isNotifying
                  ? "bg-indigo-700 text-white"
                  : isSubscribed
                  ? "bg-green-800 text-green-300 hover:bg-green-700"
                  : notificationError
                  ? "bg-red-800 text-red-300 hover:bg-red-700"
                  : "bg-gray-700 hover:bg-gray-600 text-gray-300"
              }`}
              disabled={isNotifying || isPast}
              aria-label={
                isPast
                  ? "Class already ended"
                  : isSubscribed
                  ? subscriptionType === "session"
                    ? "Turn off notifications for this class"
                    : subscriptionType === "subject"
                    ? `Turn off notifications for ${formattedSubject} classes`
                    : subscriptionType === "grade"
                    ? `Turn off notifications for Grade ${grade.replace(
                        "class_",
                        ""
                      )} classes`
                    : "Turn off notifications"
                  : "Get notified"
              }
              title={
                isPast
                  ? "Class already ended"
                  : isSubscribed
                  ? subscriptionType === "session"
                    ? "Turn off notifications for this class"
                    : subscriptionType === "subject"
                    ? `Turn off notifications for ${formattedSubject} classes`
                    : subscriptionType === "grade"
                    ? `Turn off notifications for Grade ${grade.replace(
                        "class_",
                        ""
                      )} classes`
                    : "Turn off notifications"
                  : "Get notified"
              }
            >
              {isNotifying ? (
                <div className="flex items-center justify-center w-full h-full">
                  <Loader2 size={16} className="animate-spin text-white" />
                </div>
              ) : isPast ? (
                <BellOff size={16} className="bell-icon disabled" />
              ) : isSubscribed ? (
                <>
                  <BellRing size={16} className="bell-icon subscribed" />
                  {!isNotifying && !notificationError && (
                    <ChevronDown
                      size={12}
                      className={`ml-0.5 transition-transform ${
                        showMenu ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </>
              ) : notificationError ? (
                <BellOff size={16} className="bell-icon error" />
              ) : (
                <div className="flex items-center">
                  <Bell size={16} className="bell-icon" />
                  {!isSubscribed && !notificationError && !isNotifying && (
                    <ChevronDown
                      size={12}
                      className={`ml-0.5 transition-transform ${
                        showMenu ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </div>
              )}
            </button>

            {/* Subscription/Unsubscription Options Menu */}
            {showMenu && !isNotifying && !isPast && (
              <div className="subscription-menu">
                {isSubscribed ? (
                  <>
                    {/* Only show unsubscribe options based on the subscription type */}
                    {subscriptionType === "session" && (
                      <button
                        onClick={(e) => handleNotify(e, "session")}
                        className="subscription-option"
                      >
                        <CalendarIcon size={14} className="option-icon mr-1" />
                        <span>Unsubscribe from this class only</span>
                      </button>
                    )}
                    {subscriptionType === "subject" && (
                      <button
                        onClick={(e) => handleNotify(e, "subject")}
                        className="subscription-option"
                      >
                        <Book size={14} className="option-icon mr-1" />
                        <span>
                          Unsubscribe from all {formattedSubject} classes
                        </span>
                      </button>
                    )}
                    {subscriptionType === "grade" && (
                      <button
                        onClick={(e) => handleNotify(e, "grade")}
                        className="subscription-option"
                      >
                        <GraduationCap size={14} className="option-icon mr-1" />
                        <span>
                          Unsubscribe from all Grade{" "}
                          {grade.replace("class_", "")} classes
                        </span>
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={(e) => handleNotify(e)}
                      className="subscription-option"
                    >
                      <CalendarIcon size={14} className="option-icon mr-1" />
                      <span>This Class Only</span>
                    </button>
                    <button
                      onClick={(e) => handleNotify(e, "subject")}
                      className="subscription-option"
                    >
                      <Book size={14} className="option-icon mr-1" />
                      <span>All {formattedSubject} Classes</span>
                    </button>
                    <button
                      onClick={(e) => handleNotify(e, "grade")}
                      className="subscription-option"
                    >
                      <GraduationCap size={14} className="option-icon mr-1" />
                      <span>
                        All Grade {grade.replace("class_", "")} Classes
                      </span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <Link
            href={meeting_link}
            target="_blank"
            className="flex-grow"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`w-full h-9 px-3 rounded-md text-sm font-medium transition-colors duration-300 flex items-center justify-center ${
                isPast
                  ? "bg-purple-600 hover:bg-purple-700 text-white"
                  : isLive
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-indigo-600 hover:bg-indigo-700 text-white"
              }`}
            >
              {isPast ? (
                <>
                  <span>Watch Recording</span>
                  <PlayCircle size={14} className="ml-1" />
                </>
              ) : isLive ? (
                <>
                  <span>Join Now</span>
                  <ArrowRight size={14} className="ml-1" />
                </>
              ) : (
                "Join Class"
              )}
            </button>
          </Link>
        </div>

        {/* Inline notification toast */}
        {showInlineToast && <InlineNotificationToast />}
      </div>

      {/* Tooltip Portal */}
      {showTooltip &&
        portalContainerRef.current &&
        createPortal(
          <div
            className="notification-tooltip"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: `${tooltipPosition.top - 60}px`,
              left: `${tooltipPosition.left}px`,
              transform: "translateX(-50%)",
              zIndex: 9999,
              pointerEvents: "auto",
            }}
          >
            <div className="notification-content">
              {notificationSuccess ? (
                <div className="flex items-center">
                  <CheckCircle size={16} className="mr-2 text-green-400" />
                  <span>{notificationMessage}</span>
                </div>
              ) : notificationError ? (
                <div className="flex items-center">
                  <XCircle size={16} className="mr-2 text-red-400" />
                  <span>{notificationMessage}</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <Bell size={16} className="mr-2 text-blue-400" />
                  <span>{notificationMessage}</span>
                </div>
              )}
            </div>
          </div>,
          portalContainerRef.current
        )}

      {/* Add styles for the components */}
      <style jsx>{`
        .live-class-card {
          isolation: isolate;
          position: relative;
        }

        .past-class {
          opacity: 0.8;
        }

        .active-live-session {
          border: 1px solid #10b981 !important;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.2),
            0 4px 12px rgba(16, 185, 129, 0.2);
        }

        .status-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .status-badge.status-live {
          background-color: rgba(16, 185, 129, 0.8);
          color: white;
        }

        .status-badge.status-ended {
          background-color: rgba(156, 163, 175, 0.8);
          color: white;
        }

        .status-badge.status-upcoming {
          background-color: rgba(79, 70, 229, 0.8);
          color: white;
        }

        .pulse-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          margin-right: 5px;
          background-color: #fff;
          border-radius: 50%;
          animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .bell-icon.disabled {
          color: #6b7280;
          opacity: 0.6;
        }

        .bell-container {
          position: relative;
          z-index: 20;
        }

        .notification-btn {
          width: 52px;
          padding: 0;
        }

        .bell-icon {
          transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .bell-icon.subscribed {
          color: #a7f3d0;
          animation: bellRing 1s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
          transform-origin: top center;
        }

        .bell-icon.error {
          color: #fca5a5;
          animation: bellShake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }

        @keyframes bellRing {
          0%,
          100% {
            transform: rotate(0);
          }
          15% {
            transform: rotate(15deg);
          }
          30% {
            transform: rotate(-10deg);
          }
          45% {
            transform: rotate(5deg);
          }
          60% {
            transform: rotate(-5deg);
          }
          75% {
            transform: rotate(2deg);
          }
        }

        @keyframes bellShake {
          0%,
          100% {
            transform: translateX(0);
          }
          25% {
            transform: translateX(-3px);
          }
          50% {
            transform: translateX(3px);
          }
          75% {
            transform: translateX(-2px);
          }
        }

        .subscription-menu {
          position: absolute;
          bottom: calc(100% + 5px); /* Position ABOVE the button */
          left: 0;
          background: #1f2937;
          border-radius: 8px;
          overflow: hidden;
          width: 220px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          border: 1px solid #374151;
          animation: slideUp 0.2s ease-out forwards;
          z-index: 30;
          opacity: 0;
          transform: translateY(8px);
        }

        .subscription-option {
          display: flex;
          align-items: center;
          width: 100%;
          text-align: left;
          padding: 10px 12px;
          background: transparent;
          border: none;
          color: #e5e7eb;
          font-size: 13px;
          border-bottom: 1px solid #374151;
          transition: all 0.15s ease;
        }

        .subscription-option:last-child {
          border-bottom: none;
        }

        .subscription-option:hover {
          background: #2d3748;
        }

        .option-icon {
          margin-right: 8px;
          flex-shrink: 0;
          opacity: 0.7;
          transition: all 0.2s ease;
        }

        .subscription-option:hover .option-icon {
          opacity: 1;
          transform: scale(1.1);
        }

        @keyframes slideUp {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Inline toast styles */
        .inline-notification {
          position: relative;
          width: 100%;
          margin-top: 10px;
        }

        .inline-toast {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 10px 14px;
          border-radius: 6px;
          background: #1e293b;
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
          animation: slideInUp 0.3s ease forwards;
        }

        .inline-toast.success {
          border-left: 4px solid #10b981;
        }

        .inline-toast.error {
          border-left: 4px solid #ef4444;
        }

        .inline-toast.info {
          border-left: 4px solid #6366f1;
        }

        .toast-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 10px;
          width: 16px;
          height: 16px;
        }

        .toast-message {
          margin: 0;
          padding: 0;
          color: #f1f5f9;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.3;
          flex: 1;
          display: inline-block;
          vertical-align: middle;
          white-space: normal;
        }

        .success .toast-icon {
          color: #34d399;
        }

        .error .toast-icon {
          color: #f87171;
        }

        .info .toast-icon {
          color: #818cf8;
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Tooltip styles */
        .notification-tooltip {
          width: auto;
          max-width: 300px;
          animation: fadeInNotification 0.3s ease-out forwards;
        }

        .notification-content {
          background-color: rgba(17, 24, 39, 0.95);
          color: #f3f4f6;
          padding: 10px 14px;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
          font-size: 13px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          white-space: nowrap;
        }

        @keyframes fadeInNotification {
          from {
            opacity: 0;
            transform: translateY(10px) translateX(-50%);
          }
          to {
            opacity: 1;
            transform: translateY(0) translateX(-50%);
          }
        }

        @media (prefers-color-scheme: dark) {
          .notification-content {
            background-color: rgba(17, 24, 39, 0.95);
            color: #f3f4f6;
          }
        }
      `}</style>
    </div>
  );
}

// Note: You'll need to implement the unsubscribeFromLiveClass function in your notificationService.js file
